import { Delivery } from './Delivery';
import { Pk } from './Pk';

export class DrugModel {
  private _code: string;
  private _name: string;
  private _info: string[];
  private _drug: string;
  private _href: string;
  private _delivery: Delivery | null;
  private _population: string;
  private _age: {
    range: number[];
    unit: string;
  };
  private _weight: {
    range: number[];
    unit: string;
  };
  private _clearance: Pk[];
  private _vd: Pk[];
  private _default: any | null | undefined;
  private _usesDialysis: boolean;

  constructor(param: {
    code: string;
    name: string;
    info: string[];
    drug: string;
    href: string;
    delivery: Delivery | null;
    population: string;
    age: { range: number[]; unit: string };
    weight: { range: number[]; unit: string };
    clearance: Pk[];
    vd: Pk[];
    default: any | null | undefined;
    usesDialysis?: boolean;
  }) {
    this._code = param.code;
    this._name = param.name;
    this._info = param.info;
    this._drug = param.drug;
    this._href = param.href;
    this._delivery = param.delivery;
    this._population = param.population;
    this._age = param.age;
    this._weight = param.weight;
    this._default = param.default;
    this._clearance = param.clearance;
    this._vd = param.vd;
    this._usesDialysis = param.usesDialysis ?? false;
  }

  get code(): string {
    return this._code;
  }

  get name(): string {
    return this._name;
  }

  get info(): string[] {
    return this._info;
  }

  get drug(): string {
    return this._drug;
  }

  get href(): string {
    return this._href;
  }

  get delivery(): Delivery | null {
    return this._delivery;
  }

  get population(): string {
    return this._population;
  }

  get age(): { range: number[]; unit: string } {
    return this._age;
  }

  get weight(): { range: number[]; unit: string } {
    return this._weight;
  }

  get clearance(): Pk[] {
    return this._clearance;
  }

  get vd(): Pk[] {
    return this._vd;
  }

  get default(): any {
    return this._default;
  }

  get usesDialysis(): boolean {
    return this._usesDialysis;
  }

  getClearanceMethod(): string | null | undefined {
    const cl = this._clearance.filter((c) => c.method !== null)[0];
    return cl ? cl.method : null;
  }

  getCompartment(): number {
    let rtn = 1; //default to one-compartment
    const cl = this._clearance.filter((c) => c.type !== null);
    if (cl && cl.length > 0) {
      rtn = cl[0]!.type === 'one-compartment' ? 1 : cl.length;
    }
    return rtn;
  }

  // Calculate drug clearance by model
  // @params crcl : creatinine clearance (mL/min) or (L/h)
  // @params bsa : body surface area (m2)
  // @params weight : actual body weight (kg), used for weight-normalised formulas
  // @params dialysis : hemodialysis status (used by GOTI2018)
  // @return drugCl (L/h)
  drugClearance(crcl: number, bsa: number, weight?: number, dialysis?: boolean): number {
    let drugCl = 0;
    const clearance = this.clearance[0]!;
    switch (this.code) {
      case 'BUEL2005':
        // 'CL(Vanc)(L/h) = 1.08 × CLCR (Cockcroft-Gault modified, mL/min)'
        drugCl = crcl * clearance.coeff * (60 / 1000);
        break;
      case 'ROBERTS2011':
        // 'CL(Vanc)(L/h) = 4.58 * (CrCL (Cockcroft-Gault, mL/min) / 1.73 (m2) * BSA (m2)) / 100'
        drugCl = (clearance.coeff * ((crcl / 1.73) * bsa)) / 100;
        break;
      case 'THOM2009':
        // 'CL (L/h) = 2.99 × (1 + 0.0154 × (CrCL_mL/min − 66))'
        drugCl = 2.99 * (1 + 0.0154 * (crcl - 66));
        break;
      case 'LLOP2006':
        // 'CL (L/h) = 0.034 × CrCL (mL/min) + 0.015 × TBW (kg)'
        drugCl = 0.034 * crcl + 0.015 * (weight ?? 60);
        break;
      case 'ADAN2015':
        // 'CL (L/h) = 6.54 × (CrCL_BSA / 125), CrCL normalised to 1.73 m²'
        drugCl = (clearance.coeff * ((crcl / bsa) * 1.73)) / 125;
        break;
      case 'MASI2020':
        // 'CL (L/h) = 6.88 × (CrCL / 40)^0.69'
        drugCl = 6.88 * Math.pow(crcl / 40, 0.69);
        break;
      case 'GOTI2018': {
        // 'CL (L/h) = 4.5 × (CrCL/120)^0.8 × 0.7^DIAL; CrCL capped at 150 mL/min'
        const cappedCrcl = Math.min(crcl, 150);
        const dialFactor = dialysis ? 0.7 : 1;
        drugCl = 4.5 * Math.pow(cappedCrcl / 120, 0.8) * dialFactor;
        break;
      }
      case 'UDY2015':
        // '[Pip] CL(L/h) = 16.3 × CLCR(urinary, mL/min)/100'
        drugCl = (clearance.coeff * crcl) / 100;
        break;
      case 'GENTA_STD':
      case 'GENTA_ICU': {
        // CL (L/h) = CLm × (TBW/70) + fr × CrCl(L/h)
        const fr = this.clearance[1]!.coeff;
        const crclLh = crcl * 0.06; // mL/min → L/h
        drugCl = clearance.coeff * ((weight ?? 70) / 70) + fr * crclLh;
        break;
      }
    }
    return drugCl;
  }

  correctSCR(scr: number, ageYears?: number) {
    if (scr && this.code === 'BUEL2005') {
      return scr * 1.065 + 0.067;
    }
    if (scr && this.code === 'ADAN2015' && scr < 1) {
      return 1;
    }
    if (scr && this.code === 'GOTI2018' && scr < 1 && ageYears && ageYears > 60) {
      return 1;
    }
    return scr;
  }
}
